import { Component, inject } from '@angular/core';
import { DateRange, extractDateRange, extractDefaultStoreIds, HxReportService, HxUiLabelPipe, isoDate, PaymentScheduleModel, toRouterQueryParams } from 'hx-services';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HxDateRangeSelectComponent, HxLoaderComponent, HxStoreSelectComponent } from 'hx-component';
import { TranslocoPipe } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { DatePipe, DecimalPipe, NgClass, NgForOf, NgIf } from '@angular/common';

interface PaymentScheduleInfoModel extends PaymentScheduleModel {
  status?: string,
}

@Component({
  selector: 'app-payment-schedule',
  templateUrl: './payment-schedule.component.html',
  styleUrls: ['./payment-schedule.component.css'],
  imports: [
    HxDateRangeSelectComponent,
    HxStoreSelectComponent,
    TranslocoPipe,
    HxLoaderComponent,
    FormsModule,
    NgForOf,
    NgIf,
    HxUiLabelPipe,
    DatePipe,
    DecimalPipe,
    NgClass,
    RouterLink
  ]
})
export class PaymentScheduleComponent {
  params: {
    storeIds?: number[],
    date?: DateRange,
  } = {};
  isLoading = {
    list: false,
    generate: false,
  };
  list: PaymentScheduleInfoModel[] = [];

  private readonly reportService = inject(HxReportService);
  private readonly aRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);

  constructor() {
    this.aRoute.queryParamMap.pipe(takeUntilDestroyed()).subscribe(async paramMap => {
      const asArrFn = (name: string, mapFn: (key: string) => any) => paramMap.get(name)?.split(',').map(mapFn) ?? undefined;
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(new Date()), to: isoDate(new Date())};
      this.params.storeIds = asArrFn('storeIds', str => Number(str)) ?? extractDefaultStoreIds();
      if (this.params.storeIds) {
        this.load();
      }
    });
  }

  resetFilter() {
    this.params.date = {from: isoDate(new Date()), to: isoDate(new Date())};
    this.navigate();
  }

  navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  async load() {
    this.list = [];
    this.isLoading.list = true;
    try {
      this.list = await this.reportService.getPaymentSchedule({ fromDate: this.params.date.from, toDate: this.params.date.to, storeIds: this.params.storeIds });
      this.list.map(item => {
        if (!item.paidDate && item.payBefore > isoDate(new Date())) {
          item.status = 'planned';
        } else if (!item.paidDate && item.payBefore <= isoDate(new Date())) {
          item.status = 'expired';
        } else if (item.paidDate) {
          item.status = 'paid';
        }
      });
    } finally {
      this.isLoading.list = false;
    }
  }
}
